// Pure helper: produce a log-safe copy of any value.
// - arrays longer than SAMPLE_LIMIT collapse to a { count, sample, truncated } summary
// - strings longer than MAX_STRING are truncated
// - object graphs deeper than MAX_DEPTH stop with '[max depth]'
// - keys matching SECRET_KEYS are replaced with '[redacted]'
// - true cycles (an object that is its own ancestor) become '[circular]'
import type { LogEntry } from './types';

const MAX_DEPTH = 6;
const MAX_STRING = 2000;
const SAMPLE_LIMIT = 5;
const SECRET_KEYS = ['password', 'pass', 'token', 'authorization', 'cookie', 'secret', 'apikey', 'api_key', 'jwt', 'sessionid', 'credential'];
// Keys that contain a secret substring but carry no secret — exact-match (lower-case)
// allowlist checked before SECRET_KEYS. e.g. the diagnostics flag `cookiesEnabled`
// matches the `cookie` pattern but is just a boolean.
const SAFE_KEYS = new Set(['cookiesenabled']);

function isSecretKey(key: string): boolean {
  const k = key.toLowerCase();
  if (SAFE_KEYS.has(k)) return false;
  return SECRET_KEYS.some((s) => k.includes(s));
}

export function redact(value: unknown): unknown {
  return redactInner(value, 0, new Set<object>());
}

function redactInner(value: unknown, depth: number, ancestors: Set<object>): unknown {
  if (typeof value === 'string') {
    return value.length > MAX_STRING
      ? value.slice(0, MAX_STRING) + `…(+${value.length - MAX_STRING} chars)`
      : value;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  const obj = value as object;
  if (ancestors.has(obj)) {
    return '[circular]';
  }
  if (depth >= MAX_DEPTH) {
    return '[max depth]';
  }

  ancestors.add(obj);
  let result: unknown;

  if (Array.isArray(value)) {
    if (value.length > SAMPLE_LIMIT) {
      result = {
        __summary: true,
        count: value.length,
        sample: value.slice(0, SAMPLE_LIMIT).map((v) => redactInner(v, depth + 1, ancestors)),
        truncated: value.length - SAMPLE_LIMIT,
      };
    } else {
      result = value.map((v) => redactInner(v, depth + 1, ancestors));
    }
  } else {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = isSecretKey(k) ? '[redacted]' : redactInner(v, depth + 1, ancestors);
    }
    result = out;
  }

  // Only an ancestor (not a sibling) should count as circular, so drop it on the way out.
  ancestors.delete(obj);
  return result;
}

/**
 * Scrub secret-named parameters out of an x-www-form-urlencoded body string
 * (e.g. "username=x&password=y" → "username=x&password=%5Bredacted%5D").
 * Returns the input unchanged if it parses to nothing secret. Logging-only.
 */
export function redactUrlEncoded(body: string): string {
  try {
    const params = new URLSearchParams(body);
    let changed = false;
    for (const key of Array.from(params.keys())) {
      if (isSecretKey(key)) {
        params.set(key, '[redacted]');
        changed = true;
      }
    }
    return changed ? params.toString() : body;
  } catch {
    return body;
  }
}

// Re-export for convenience so consumers can `import { redact, LogEntry } from './redact'` if desired.
export type { LogEntry };
